import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import {
  Send,
  Eye,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Calendar,
  Mail,
  Copy,
  Trash2,
  Edit,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  RefreshCw,
  Users,
  Bot,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { PreApprovalData, PreApprovalListItem, PreApprovalAnalytics, RecipientSummary, PreApprovalMetadata } from '@/types/pre-approval';
import { SupabasePreApprovalService } from '@/services/supabasePreApprovalService';
import { userPreferencesService } from '@/services/userPreferencesService';
import { formatCurrency } from '@/utils/formatCurrency';
import { ContactSelectionModal } from '@/components/pre-approval/ContactSelectionModal';
import type { Contact } from '@/types/contacts-new';
import { PreApprovalSendModal } from './PreApprovalSendModal';
import { RecipientStatusModal } from './RecipientStatusModal';
import { RecipientBadgeGroup } from './RecipientBadgeGroup';
import { ConsumerAccordionRow } from './ConsumerAccordionRow';
import { groupPreApprovalsByConsumer, getGroupingSummary } from '@/utils/preApprovalGrouping';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { logger } from '@/utils/serviceLogger';
import { debounce } from '@/utils/debounce';
import { openWindow } from '@/utils/windowOpenHelper';
import { TechnologyStatusBar } from '@/components/ui/TechnologyStatusBar';
import { createPreApprovalCloneDraft } from '@/utils/pre-approval/cloneSanitizer';

/**
 * Extract recipient summary from pre-approval metadata for dashboard display
 */
function extractRecipientSummary(metadata?: PreApprovalMetadata): RecipientSummary | undefined {
  if (!metadata?.recipients || metadata.recipients.length === 0) {
    return undefined;
  }

  const summary: RecipientSummary = {};

  for (const recipient of metadata.recipients) {
    const hasViewed = !!recipient.firstViewedAt;
    const recipientData = {
      status: hasViewed ? ('viewed' as const) : ('sent' as const),
      name: recipient.name,
      email: recipient.email,
      phone: recipient.phone ?? null,
      viewCount: recipient.viewCount,
      lastViewedAt: recipient.lastViewedAt
    };

    switch (recipient.type) {
      case 'borrower':
        summary.borrower = recipientData;
        break;
      case 'co_borrower':
        summary.co_borrower = recipientData;
        break;
      case 'partner':
        if (!summary.partner) {
          summary.partner = [];
        }
        summary.partner.push(recipientData);
        break;
    }
  }

  return summary;
}

interface PreApprovalDashboardProps {
  onCreatePreApproval: (prefilledData?: Partial<PreApprovalData>) => void;
}

export const PreApprovalDashboard: React.FC<PreApprovalDashboardProps> = ({
  onCreatePreApproval
}) => {
  const _navigate = useNavigate();
  
  // State management with proper typing
  const [preApprovals, setPreApprovals] = useState<PreApprovalData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('month');
  const [showContactSelectionModal, setShowContactSelectionModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [analytics, setAnalytics] = useState<PreApprovalAnalytics | null>(null);
  const [selectedPreApproval, setSelectedPreApproval] = useState<PreApprovalData | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [isResend, setIsResend] = useState(false);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [recipientModalPreApproval, setRecipientModalPreApproval] = useState<PreApprovalListItem | null>(null);

  // Consumer grouping state
  const [expandedConsumers, setExpandedConsumers] = useState<Set<string>>(new Set());

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [_lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  // Service health monitoring (loose shape — consumed by TechnologyStatusBar)
  const [serviceHealth, setServiceHealth] = useState<Record<string, unknown> | null>(null);

  /**
   * Load user preferences on mount
   */
  useEffect(() => {
    const loadPreferences = async () => {
      const correlationId = `dashboard-${Date.now()}`;
      try {
        setIsLoadingPreferences(true);
        const result = await userPreferencesService.getPreferences();
        
        if (result.success && result.data.filterPreferences?.preApprovals) {
          const prefs = result.data.filterPreferences.preApprovals;
          
          // Apply saved preferences
          if (prefs.status?.length) {
            setSelectedStatus(prefs.status.length === 1 ? prefs.status[0] : 'all');
          }
          if (prefs.dateRange) {
            setDateRange(prefs.dateRange);
          }
          if (prefs.customDateRange) {
            setCustomDateRange({
              from: prefs.customDateRange.start ? parseAsLocalDate(prefs.customDateRange.start) : undefined,
              to: prefs.customDateRange.end ? parseAsLocalDate(prefs.customDateRange.end) : undefined
            });
          }
          if (prefs.sortBy) {
            setSortField(prefs.sortBy);
          }
          if (prefs.sortOrder) {
            setSortDirection(prefs.sortOrder);
          }
        }
      } catch (__error) {
        logger.error(
          'Failed to load user preferences',
          __error as Error,
          correlationId,
          'PreApprovalDashboard',
          'loadPreferences'
        );
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    loadPreferences();
  }, []);
  
  /**
   * Load pre-approvals and analytics
   */
  const loadPreApprovals = useCallback(async () => {
    const correlationId = `load-${Date.now()}`;
    try {
      setError(null);

      // Fix: Use getCurrentUserProfile() to respect impersonation
      // This ensures dashboard shows pre-approvals created during impersonation
      const { getCurrentUserProfile } = await import('@/utils/profileUtils');
      const profile = await getCurrentUserProfile();

      // Fetch pre-approvals and analytics in parallel
      const [preApprovalData, analyticsData] = await Promise.all([
        SupabasePreApprovalService.getAllPreApprovals(profile.id),
        SupabasePreApprovalService.getAnalytics({ loanOfficerId: profile.id })
      ]);
      
      setPreApprovals(preApprovalData);
      setAnalytics(analyticsData);
      setLastRefresh(new Date());
      
      // Update service health
      // Health metrics not available in Supabase service yet
      const health = { status: 'healthy', syncPending: 0, lastError: null };
      setServiceHealth(health);
      
    } catch (loadError) {
      const errorMessage = loadError instanceof Error ? loadError.message : 'Failed to load pre-approvals';
      setError(errorMessage);
      logger.error(
        'Failed to load pre-approvals',
        loadError as Error,
        correlationId,
        'PreApprovalDashboard',
        'loadPreApprovals'
      );
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Initial load and refresh interval
  useEffect(() => {
    if (!isLoadingPreferences) {
      loadPreApprovals();
      
      // Set up auto-refresh every 30 seconds
      const interval = setInterval(loadPreApprovals, 30000);
      return () => clearInterval(interval);
    }
  }, [loadPreApprovals, isLoadingPreferences]);

  /**
   * Save filter preferences when they change (debounced)
   */
  const saveFilterPreferences = useMemo(
    () => debounce(async () => {
      const correlationId = `save-prefs-${Date.now()}`;
      try {
        // Build status array based on selected status
        const statusArray = selectedStatus === 'all' 
          ? undefined 
          : [selectedStatus as 'draft' | 'sent' | 'viewed' | 'accepted' | 'expired'];
        
        await userPreferencesService.updateFilterPreferences({
          preApprovals: {
            status: statusArray,
            dateRange: dateRange as 'today' | 'week' | 'month' | 'all' | 'custom',
            customDateRange: dateRange === 'custom' && (customDateRange.from || customDateRange.to) ? {
              start: customDateRange.from?.toISOString() || '',
              end: customDateRange.to?.toISOString() || ''
            } : undefined,
            sortBy: sortField as 'createdAt' | 'sentAt' | 'borrowerName' | 'status',
            sortOrder: sortDirection
          },
          lastUpdated: new Date().toISOString()
        });
        
      } catch (saveError) {
        logger.error(
          'Failed to save filter preferences',
          saveError as Error,
          correlationId,
          'PreApprovalDashboard',
          'saveFilterPreferences'
        );
      }
    }, 500),
    [selectedStatus, dateRange, customDateRange, sortField, sortDirection]
  );
  
  useEffect(() => {
    if (!isLoadingPreferences) {
      saveFilterPreferences();
    }
  }, [saveFilterPreferences, isLoadingPreferences]);

  /**
   * Memoized filtered and sorted pre-approvals with ML-ready tracking
   */
  const filteredPreApprovals = useMemo(() => {
    let filtered = [...preApprovals];
    
    // Track filter usage for analytics
    const filterMetrics = {
      hasSearch: !!searchTerm,
      hasStatusFilter: selectedStatus !== 'all',
      hasDateFilter: dateRange !== 'all',
      resultCount: 0
    };

    // Search filter with fuzzy matching preparation
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(pa => {
        const borrower = pa.borrowerInfo || pa.borrower;
        const borrowerName = `${borrower?.firstName || ''} ${borrower?.lastName || ''}`.toLowerCase();
        const email = (borrower?.email || '').toLowerCase();
        const phone = borrower?.phone || '';
        
        return borrowerName.includes(searchLower) || 
               email.includes(searchLower) ||
               phone.includes(searchLower) ||
               pa.id.includes(searchTerm);
      });
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(pa => pa.status === selectedStatus);
    }

    // Date range filter with custom logic
    if (dateRange === 'custom' && (customDateRange.from || customDateRange.to)) {
      filtered = filtered.filter(pa => {
        const paDate = new Date(pa.createdAt);
        if (customDateRange.from && paDate < customDateRange.from) {
return false;
}
        if (customDateRange.to && paDate > customDateRange.to) {
return false;
}
        return true;
      });
    } else if (dateRange !== 'all' && dateRange !== 'custom') {
      const now = new Date();
      const startDate = new Date();
      
      switch (dateRange) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtered = filtered.filter(pa => 
        new Date(pa.createdAt) >= startDate
      );
    }

    // Advanced sorting with null handling
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'borrowerName': {
          const aBorrower = a.borrowerInfo || a.borrower;
          const bBorrower = b.borrowerInfo || b.borrower;
          aValue = `${aBorrower?.firstName || ''} ${aBorrower?.lastName || ''}`.toLowerCase();
          bValue = `${bBorrower?.firstName || ''} ${bBorrower?.lastName || ''}`.toLowerCase();
          break;
        }
          
        case 'preApprovalAmount':
        case 'amount':
          aValue = a.loanDetails?.preApprovalAmount || a.loanDetails?.loanAmount || 0;
          bValue = b.loanDetails?.preApprovalAmount || b.loanDetails?.loanAmount || 0;
          break;
          
        case 'createdAt':
        case 'sentAt':
        case 'viewedAt':
          aValue = a[sortField as keyof PreApprovalData] ? new Date(a[sortField as keyof PreApprovalData] as string).getTime() : 0;
          bValue = b[sortField as keyof PreApprovalData] ? new Date(b[sortField as keyof PreApprovalData] as string).getTime() : 0;
          break;
          
        default: {
          const aRaw = a[sortField as keyof PreApprovalData];
          const bRaw = b[sortField as keyof PreApprovalData];
          aValue = typeof aRaw === 'number' ? aRaw : String(aRaw ?? '');
          bValue = typeof bRaw === 'number' ? bRaw : String(bRaw ?? '');
        }
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    filterMetrics.resultCount = filtered.length;
    
    // Log filter metrics for ML training
    if (filterMetrics.hasSearch || filterMetrics.hasStatusFilter || filterMetrics.hasDateFilter) {
      logger.info(
        'Pre-approval filters applied',
        `filter-${Date.now()}`,
        'PreApprovalDashboard',
        'filteredPreApprovals',
        filterMetrics
      );
    }

    return filtered;
  }, [preApprovals, searchTerm, selectedStatus, dateRange, customDateRange, sortField, sortDirection]);

  /**
   * Convert PreApprovalData to PreApprovalListItem for UI
   */
  const preApprovalListItems: PreApprovalListItem[] = useMemo(() => {
    return filteredPreApprovals.map(pa => ({
      id: pa.id,
      borrowerName: `${(pa.borrowerInfo || pa.borrower)?.firstName || ''} ${(pa.borrowerInfo || pa.borrower)?.lastName || ''}`.trim(),
      borrowerEmail: (pa.borrowerInfo || pa.borrower)?.email || '',
      preApprovalAmount: pa.loanDetails?.preApprovalAmount || pa.loanDetails?.loanAmount || 0,
      status: pa.status,
      createdAt: pa.createdAt,
      sentAt: pa.sentAt,
      viewedAt: pa.viewedAt,
      acceptedAt: pa.acceptedAt,
      expiresAt: pa.expiresAt,
      viewCount: pa.viewCount,
      recipientsSummary: extractRecipientSummary(pa.metadata),
      sentByWisr: pa.source === 'wisr_sms',
      expiredAt: pa.expiredAt,
      expiredReason: pa.expiredReason,
      contactId: pa.contactId
    }));
  }, [filteredPreApprovals]);

  /**
   * Group pre-approvals by consumer for accordion view
   */
  const consumerGroups = useMemo(() =>
    groupPreApprovalsByConsumer(preApprovalListItems),
    [preApprovalListItems]
  );

  /**
   * Get grouping summary for display
   */
  const groupingSummary = useMemo(() =>
    getGroupingSummary(consumerGroups),
    [consumerGroups]
  );

  /**
   * Toggle consumer accordion expansion
   */
  const toggleConsumer = useCallback((consumerKey: string) => {
    setExpandedConsumers(prev => {
      const next = new Set(prev);
      if (next.has(consumerKey)) {
        next.delete(consumerKey);
      } else {
        next.add(consumerKey);
      }
      return next;
    });
  }, []);

  /**
   * Handle viewing a pre-approval
   */
  const handleViewPreApproval = useCallback(async (preApproval: PreApprovalListItem) => {
    try {
      // LO preview link - isolated, doesn't track as borrower view
      // Same link that LO receives in their confirmation email
      const accessUrl = `${window.location.origin}/pre-approval/${preApproval.id}/preview?lo=true`;

      // Open the preview URL
      openWindow(accessUrl);
    } catch (__error) {
      logger.error('Error viewing pre-approval:', __error);
      toast.error('Failed to open pre-approval preview');
    }
  }, []);

  /**
   * Handle editing a pre-approval
   */
  const handleEditPreApproval = useCallback(async (preApproval: PreApprovalListItem) => {
    try {
      // Load full pre-approval data
      const fullPreApproval = await SupabasePreApprovalService.getPreApprovalById(preApproval.id);
      if (fullPreApproval) {
        // Use the same navigation pattern as create
        onCreatePreApproval(fullPreApproval);
      } else {
        toast.error('Pre-approval not found');
      }
    } catch {
      toast.error('Failed to load pre-approval for editing');
    }
  }, [onCreatePreApproval]);

  /**
   * Handle sending a pre-approval with proper data loading
   */
  const handleSendPreApproval = useCallback(async (preApproval: PreApprovalListItem, resend: boolean = false) => {
    const correlationId = `send-${Date.now()}`;
    try {
      // Load full pre-approval data
      const fullPreApproval = await SupabasePreApprovalService.getPreApprovalById(preApproval.id);
      if (fullPreApproval) {
        setSelectedPreApproval(fullPreApproval);
        setIsResend(resend);
        setShowSendModal(true);
      } else {
        toast.error('Pre-approval not found');
      }
    } catch (loadError) {
      logger.error(
        'Failed to load pre-approval for sending',
        loadError as Error,
        correlationId,
        'PreApprovalDashboard',
        'handleSendPreApproval'
      );
      toast.error('Failed to load pre-approval');
    }
  }, []);

  /**
   * Handle deleting a pre-approval with confirmation
   */
  const handleDeletePreApproval = useCallback(async (preApproval: PreApprovalListItem) => {
    // eslint-disable-next-line no-alert -- existing UX; replace with a Dialog confirmation in a follow-up
    if (!window.confirm(`Are you sure you want to delete this pre-approval for ${preApproval.borrowerName}?`)) {
      return;
    }
    
    const correlationId = `delete-${Date.now()}`;
    try {
      // Optimistically update the UI by removing the item from state
      setPreApprovals(prev => prev.filter(pa => pa.id !== preApproval.id));
      
      // Update analytics immediately
      if (analytics) {
        const deletedPreApproval = preApprovals.find(pa => pa.id === preApproval.id);
        if (deletedPreApproval) {
          const updatedAnalytics = { ...analytics };
          updatedAnalytics.totalPreApprovals -= 1;
          
          // Update month count if created this month
          const now = new Date();
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          if (new Date(deletedPreApproval.createdAt) >= startOfMonth) {
            updatedAnalytics.totalThisMonth = (updatedAnalytics.totalThisMonth || 1) - 1;
          }
          
          // Update status-based counts
          if (deletedPreApproval.status === 'sent' || deletedPreApproval.status === 'viewed' || deletedPreApproval.status === 'accepted') {
            updatedAnalytics.preApprovalsSent -= 1;
          }
          if (deletedPreApproval.status === 'viewed' || deletedPreApproval.status === 'accepted') {
            updatedAnalytics.preApprovalsViewed -= 1;
          }
          if (deletedPreApproval.status === 'accepted') {
            updatedAnalytics.preApprovalsAccepted = (updatedAnalytics.preApprovalsAccepted || 1) - 1;
          }
          
          setAnalytics(updatedAnalytics);
        }
      }
      
      await SupabasePreApprovalService.deletePreApproval(preApproval.id);
      
      toast.success('Pre-approval deleted successfully');
      
      logger.info(
        'Pre-approval deleted',
        correlationId,
        'PreApprovalDashboard',
        'handleDeletePreApproval',
        { preApprovalId: preApproval.id }
      );
      
      // Refresh data in the background to ensure sync
      // Add a small delay to ensure database commit
      setTimeout(() => {
        loadPreApprovals();
      }, 500);
    } catch (deleteError) {
      logger.error(
        'Failed to delete pre-approval',
        deleteError as Error,
        correlationId,
        'PreApprovalDashboard',
        'handleDeletePreApproval'
      );
      toast.error('Failed to delete pre-approval');

      // Reload data on error to restore the correct state
      await loadPreApprovals();
    }
  }, [loadPreApprovals, analytics, preApprovals]);

  const handleDeactivatePreApproval = useCallback(async (preApproval: PreApprovalListItem) => {
    // eslint-disable-next-line no-alert -- existing UX; replace with a Dialog confirmation in a follow-up
    if (!window.confirm(`Deactivate this pre-approval for ${preApproval.borrowerName}? Borrower links will stop working, but history will be preserved.`)) {
      return;
    }

    try {
      await SupabasePreApprovalService.deactivatePreApproval(preApproval.id);
      toast.success('Pre-approval deactivated');
      await loadPreApprovals();
    } catch (deactivateError) {
      logger.error('Failed to deactivate pre-approval:', deactivateError);
      toast.error('Failed to deactivate pre-approval');
    }
  }, [loadPreApprovals]);

  /**
   * Handle cloning a pre-approval
   */
  const handleClonePreApproval = useCallback(async (preApproval: PreApprovalListItem) => {
    const correlationId = `clone-${Date.now()}`;
    try {
      // Load the full pre-approval
      const fullPreApproval = await SupabasePreApprovalService.getPreApprovalById(preApproval.id);
      if (!fullPreApproval) {
        toast.error('Pre-approval not found');
        return;
      }
      
      const cloned = createPreApprovalCloneDraft(fullPreApproval);
      
      // Save the cloned pre-approval
      await SupabasePreApprovalService.savePreApproval(cloned);
      
      await loadPreApprovals();
      toast.success('Pre-approval cloned successfully');
      
      // Navigate to edit the cloned pre-approval using the state-based navigation
      onCreatePreApproval(cloned);
      
      logger.info(
        'Pre-approval cloned',
        correlationId,
        'PreApprovalDashboard',
        'handleClonePreApproval',
        { originalId: preApproval.id, clonedId: cloned.id }
      );
    } catch (cloneError) {
      logger.error(
        'Failed to clone pre-approval',
        cloneError as Error,
        correlationId,
        'PreApprovalDashboard',
        'handleClonePreApproval'
      );
      toast.error('Failed to clone pre-approval');
    }
  }, [loadPreApprovals, onCreatePreApproval]);

  const getStatusBadge = (status: PreApprovalData['status']) => {
    const statusConfig = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
      sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700' },
      viewed: { label: 'Viewed', className: 'bg-green-100 text-green-700' },
      accepted: { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-700' }
    };

    const config = statusConfig[status];
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  /**
   * Handle sorting with telemetry
   */
  const handleSort = useCallback((field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    
    // Track sort usage for UX optimization
    logger.info(
      'Sort field changed',
      `sort-${Date.now()}`,
      'PreApprovalDashboard',
      'handleSort',
      { field, previousField: sortField }
    );
  }, [sortField]);

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4" /> 
      : <ArrowDown className="w-4 h-4" />;
  };

  /**
   * Format date as MM/DD/YY and HH:MM AM/PM stacked
   */
  const formatDateTime = useCallback((dateString?: string) => {
    if (!dateString) {
      return <span>-</span>;
    }

    const date = new Date(dateString);

    // Format date part: MM/DD/YY
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    const datePart = `${month}/${day}/${year}`;

    // Format time part: HH:MM AM/PM
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const timePart = `${hours}:${minutes} ${ampm}`;

    return (
      <div className="flex flex-col">
        <span className="font-medium">{datePart}</span>
        <span className="text-xs text-muted-foreground">{timePart}</span>
      </div>
    );
  }, []);

  // Handle contact selection for new pre-approval
  const handleContactSelected = useCallback(async (contact: Contact) => {
    setShowContactSelectionModal(false);
    
    // Create pre-approval data with selected contact
    const preApprovalData = {
      borrowerInfo: {
        salutation: contact.title, // Pass title as salutation for formal addressing
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email || '',
        phone: contact.mobilePhone || contact.homePhone || contact.workPhone || ''
      },
      contactId: contact.id
    };
    
    // Call the original onCreatePreApproval with pre-filled data
    onCreatePreApproval(preApprovalData);
  }, [onCreatePreApproval]);

  // Show loading skeleton while initial data loads
  if (isLoading || isLoadingPreferences) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header: Title + System Status + Create Button */}
      <div className="flex items-center justify-between gap-4 -mt-6 -mx-6 px-6 py-4 bg-background border-b mb-6">
        {/* Left: Page Title */}
        <h1 className="text-2xl font-bold text-foreground shrink-0">Pre-Approval</h1>

        {/* Center: System Status HUD */}
        <div className="flex-1 flex justify-center">
          <TechnologyStatusBar includeRealtime={false} />
        </div>

        {/* Right: Create Button */}
        <Button onClick={() => setShowContactSelectionModal(true)} className="bg-[#8B5CF6] hover:bg-[#7C3AED] shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Create Pre-Approval
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadPreApprovals}
              className="ml-4"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Service Health Warning */}
      {serviceHealth?.circuitBreaker?.isOpen && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            The pre-approval service is experiencing issues. Some features may be unavailable.
          </AlertDescription>
        </Alert>
      )}
      

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Pre-Approvals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.totalPreApprovals}</div>
              <p className="text-xs text-muted-foreground">
                {analytics.totalThisMonth} this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pre-Approvals Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.preApprovalsSent}</div>
              <div className="flex items-center text-xs text-muted-foreground">
                <Send className="w-3 h-3 mr-1" />
                Delivered to borrowers
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pre-Approvals Viewed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.preApprovalsViewed}</div>
              <div className="flex items-center text-xs text-muted-foreground">
                <Eye className="w-3 h-3 mr-1" />
                Opened by borrowers
              </div>
            </CardContent>
          </Card>

        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>

        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex-1 sm:flex-none">
                <Filter className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Status: </span>
                {selectedStatus === 'all' ? 'All' : selectedStatus}
              </Button>
            </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setSelectedStatus('all')}>
              All Statuses
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setSelectedStatus('draft')}>
              Draft
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSelectedStatus('sent')}>
              Sent
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSelectedStatus('viewed')}>
              Viewed
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSelectedStatus('accepted')}>
              Accepted
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSelectedStatus('expired')}>
              Expired
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex-1 sm:flex-none">
                <Calendar className="w-4 h-4 mr-2" />
                <span className="sm:hidden">
                  {dateRange === 'all' ? 'All' : 
                   dateRange === 'today' ? '1D' :
                   dateRange === 'week' ? '7D' :
                   dateRange === 'month' ? '30D' : '1Y'}
                </span>
                <span className="hidden sm:inline">
                  {dateRange === 'all' ? 'All Time' : 
                   dateRange === 'today' ? 'Today' :
                   dateRange === 'week' ? 'Last 7 Days' :
                   dateRange === 'month' ? 'Last 30 Days' : 
                   dateRange === 'year' ? 'Last Year' : 'Custom Range'}
                </span>
              </Button>
            </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setDateRange('all')}>
              All Time
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDateRange('today')}>
              Today
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDateRange('week')}>
              Last 7 Days
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDateRange('month')}>
              Last 30 Days
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDateRange('year')}>
              Last Year
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDateRange('custom')}>
              Custom Range...
            </DropdownMenuItem>
          </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {dateRange === 'custom' && (
          <DateRangePicker
            date={customDateRange}
            onDateChange={setCustomDateRange}
            className="flex-1 sm:flex-none"
          />
        )}
      </div>

      {/* Results Count */}
      {consumerGroups.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Showing {groupingSummary.totalConsumers} consumer{groupingSummary.totalConsumers !== 1 ? 's' : ''} ({groupingSummary.totalPreApprovals} pre-approval{groupingSummary.totalPreApprovals !== 1 ? 's' : ''})
          {groupingSummary.consumersWithMultiple > 0 && (
            <span className="ml-1">
              - {groupingSummary.consumersWithMultiple} with history
            </span>
          )}
        </div>
      )}

      {/* Pre-Approvals Table - Desktop (Grouped by Consumer) */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('borrowerName')}
                >
                  <div className="flex items-center gap-1">
                    Consumer
                    {getSortIcon('borrowerName')}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('preApprovalAmount')}
                >
                  <div className="flex items-center gap-1">
                    Latest Amount
                    {getSortIcon('preApprovalAmount')}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1">
                    Created
                    {getSortIcon('createdAt')}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('sentAt')}
                >
                  <div className="flex items-center gap-1">
                    Sent
                    {getSortIcon('sentAt')}
                  </div>
                </TableHead>
                <TableHead className="w-[12%]">
                  Recipients
                </TableHead>
                <TableHead className="w-[10%]">
                  History
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consumerGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    {searchTerm || selectedStatus !== 'all' || dateRange !== 'all'
                      ? 'No pre-approvals found matching your filters'
                      : 'No pre-approvals created yet. Click "Create New Pre-Approval" to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                consumerGroups.map((group) => (
                  <ConsumerAccordionRow
                    key={group.consumerKey}
                    group={group}
                    isExpanded={expandedConsumers.has(group.consumerKey)}
                    onToggle={() => toggleConsumer(group.consumerKey)}
                    onView={handleViewPreApproval}
                    onEdit={handleEditPreApproval}
                    onSend={handleSendPreApproval}
                    onClone={handleClonePreApproval}
                    onDelete={handleDeletePreApproval}
                    onViewRecipients={(pa) => {
                      setRecipientModalPreApproval(pa);
                      setShowRecipientModal(true);
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pre-Approvals Cards - Mobile (Grouped by Consumer) */}
      <div className="lg:hidden space-y-4">
        {consumerGroups.length === 0 ? (
          <Card>
            <CardContent className="text-center text-muted-foreground py-8">
              {searchTerm || selectedStatus !== 'all' || dateRange !== 'all'
                ? 'No pre-approvals found matching your filters'
                : 'No pre-approvals created yet. Click "New Pre-Approval" to get started.'}
            </CardContent>
          </Card>
        ) : (
          consumerGroups.map((group) => {
            const isExpanded = expandedConsumers.has(group.consumerKey);
            const hasHistory = group.totalCount > 1;
            const latest = group.latestPreApproval;

            return (
              <Card key={group.consumerKey} className="overflow-hidden">
                <CardContent className="p-4">
                  {/* Consumer Header - Clickable to expand */}
                  <div
                    className={`flex items-start justify-between mb-3 ${hasHistory ? 'cursor-pointer' : ''}`}
                    onClick={hasHistory ? () => toggleConsumer(group.consumerKey) : undefined}
                  >
                    <div className="flex items-center gap-2">
                      {hasHistory && (
                        <ChevronRight
                          className={`w-4 h-4 transition-transform text-muted-foreground ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{group.consumerName}</h3>
                          {latest.sentByWisr && (
                            <Badge variant="outline" className="gap-1 text-xs px-1.5 py-0 text-purple-600 border-purple-300 bg-purple-50">
                              <Bot className="w-3 h-3" />
                              Wisr
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{group.consumerEmail}</p>
                        {hasHistory && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            {group.totalCount} pre-approval{group.totalCount !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewPreApproval(latest)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Latest
                        </DropdownMenuItem>
                        {latest.status !== 'draft' && (
                          <DropdownMenuItem onClick={() => {
                            setRecipientModalPreApproval(latest);
                            setShowRecipientModal(true);
                          }}>
                            <Users className="w-4 h-4 mr-2" />
                            View Recipients
                          </DropdownMenuItem>
                        )}
                        {latest.status === 'draft' && (
                          <DropdownMenuItem onClick={() => handleEditPreApproval(latest)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {latest.status === 'draft' && (
                          <DropdownMenuItem onClick={() => handleSendPreApproval(latest, false)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Send
                          </DropdownMenuItem>
                        )}
                        {latest.status !== 'draft' && (
                          <DropdownMenuItem onClick={() => handleSendPreApproval(latest, true)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Resend
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleClonePreApproval(latest)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Clone
                        </DropdownMenuItem>
                        {['sent', 'viewed', 'accepted'].includes(latest.status) && (
                          <DropdownMenuItem
                            onClick={() => handleDeactivatePreApproval(latest)}
                            className="text-orange-600"
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeletePreApproval(latest)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Latest Pre-Approval Details */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Latest Amount</span>
                      <span className="font-semibold">{formatCurrency(latest.preApprovalAmount)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Created</span>
                      {formatDateTime(latest.createdAt)}
                    </div>

                    {latest.sentAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Sent</span>
                        {formatDateTime(latest.sentAt)}
                      </div>
                    )}

                    {latest.recipientsSummary && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Recipients</span>
                        <RecipientBadgeGroup
                          recipientsSummary={latest.recipientsSummary}
                          onBadgeClick={() => {
                            setRecipientModalPreApproval(latest);
                            setShowRecipientModal(true);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Expanded History */}
                  {isExpanded && hasHistory && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div className="text-sm font-medium text-muted-foreground">
                        Pre-Approval History
                      </div>
                      {group.preApprovals.slice(1).map((pa) => (
                        <div
                          key={pa.id}
                          className="p-3 bg-muted/30 rounded-lg space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{formatCurrency(pa.preApprovalAmount)}</span>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(pa.status)}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <MoreVertical className="w-3 h-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleViewPreApproval(pa)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View
                                  </DropdownMenuItem>
	                                  <DropdownMenuItem onClick={() => handleClonePreApproval(pa)}>
	                                    <Copy className="w-4 h-4 mr-2" />
	                                    Clone
	                                  </DropdownMenuItem>
	                                  {['sent', 'viewed', 'accepted'].includes(pa.status) && (
	                                    <DropdownMenuItem
	                                      onClick={() => handleDeactivatePreApproval(pa)}
	                                      className="text-orange-600"
	                                    >
	                                      <AlertTriangle className="w-4 h-4 mr-2" />
	                                      Deactivate
	                                    </DropdownMenuItem>
	                                  )}
	                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {pa.sentAt ? `Sent ${new Date(pa.sentAt).toLocaleDateString()}` : `Created ${new Date(pa.createdAt).toLocaleDateString()}`}
                            {pa.expiredReason === 'superseded_by_new' && ' - Superseded'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Send Modal */}
      {selectedPreApproval && (
        <PreApprovalSendModal
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSelectedPreApproval(null);
            setIsResend(false);
          }}
          preApproval={selectedPreApproval}
          isResend={isResend}
          onSend={() => {
            loadPreApprovals();
          }}
        />
      )}

      {/* Recipient Status Modal */}
      {recipientModalPreApproval && (
        <RecipientStatusModal
          isOpen={showRecipientModal}
          onClose={() => {
            setShowRecipientModal(false);
            setRecipientModalPreApproval(null);
          }}
          preApprovalId={recipientModalPreApproval.id}
          borrowerName={recipientModalPreApproval.borrowerName}
        />
      )}

      {/* Contact Selection Modal */}
      <ContactSelectionModal
        open={showContactSelectionModal}
        onOpenChange={setShowContactSelectionModal}
        onContactSelected={handleContactSelected}
      />
    </div>
  );
};
